import { Component, inject, viewChild } from '@angular/core';
import { CompetitionResultsTableService } from '../services/competition-results-table.service';
import { ResultsGridBase } from 'src/app/controls/base/results-grid.base';
import { BreadcrumbComponent } from 'src/app/controls/breadcrumb/components/breadcrumb.component';
import { GridComponent } from 'src/app/controls/grid/components/grid.component';
import { ToolbarComponent } from 'src/app/controls/toolbar/components/toolbar.component';
import { ToolbarItemDirective } from 'src/app/controls/toolbar/directives/toolbar-item.directive';
import { TranslateModule } from '@ngx-translate/core';
import { ResultSummaryComponent } from 'src/app/controls/result-summary/components/result-summary.component';
import { ResultItemDirective } from 'src/app/controls/result-summary/directives/result-item.directive';
import { DialogComponent } from 'src/app/controls/dialog/components/dialog.component';
import { GridButtonTrigger } from 'src/app/controls/grid/models/grid-button-trigger.interface';
import { RoundResult } from '../../round-result/models/round-result.interface';
import { CompetitionResult } from '../models/competition-result.interface';
import { RoundResultsTableService } from '../../round-result/services/round-results-table.service';
import { RoundService } from '../../competition-management/add-competition-card/add-round-dialog/services/round.service';
import { CompetitionCardService } from '../../competition-management/edit-competition-card/service/competition-card.service';
import { CrewDetailComponent } from '../../crew/components/crew-detail.component';

@Component({
  selector: 'app-competition-results-table',
  templateUrl: './competition-results-table.component.html',
  styleUrls: ['./competition-results-table.component.scss'],
  standalone: true,
  imports: [BreadcrumbComponent, GridComponent, ToolbarComponent, ToolbarItemDirective, TranslateModule, ResultSummaryComponent, ResultItemDirective, DialogComponent, CrewDetailComponent],
  providers: [CompetitionResultsTableService, RoundResultsTableService, RoundService, CompetitionCardService]
})
export class CompetitionResultsTableComponent extends ResultsGridBase {

  /** Crew detail dialog reference */
  protected crewDetailDialog = viewChild.required<CrewDetailComponent>(CrewDetailComponent);

  /** Competition id reference */
  protected competitionId: number | null = null;

  /** Round result reference */
  protected roundResult: RoundResult | null = null;

  /** Summary dialog key reference */
  protected dialogKey: string | null = null;

  /** Round results service reference */
  private roundResultService = inject(RoundResultsTableService);

  constructor(service: CompetitionResultsTableService) {
    super(service);
  }

  ngOnInit(): void {
    /** initializes parent logic */
    super.ngOnInit();

    /** obtains ids from route */
    this.competitionId = this.route.parent.snapshot.params.id as number;

    /** obtains display results status */
    this.loadDisplayResultsStatus(this.competitionId, true);
  }

  /**
   * Grid action handler
   * @param event the clicked event from grid
   */
  protected onButtonClick(event: GridButtonTrigger<CompetitionResult>): void {
    /** executes the button logic */
    if (event.buttonId === 'crewNumber') {
      /** opens the crew detail dialog */
      this.crewDetailDialog().show(event.row.crewNumber);
    } else {
      /** opens the stage summary dialog */
      this.openRoundSummary(event);
    }
  }

  /**
   * Opens the round summary dialog
   * @param event the button trigger
   */
  private openRoundSummary(event: GridButtonTrigger<CompetitionResult>): void {
    /** if round result is not available, do nothing */
    if (!event || !event.row || !event.row.rounds || !event.row.rounds.length) {
      return;
    }
    
    /** gets the desired round result id from dynamic column */
    const roundColumn = event.row.rounds.find(round => round.columnId === event.buttonId);

    /** if round column or round result id is not provided, do nothing */
    if (!roundColumn || !roundColumn.entityId) {
      return;
    }

    /** gets the desired round result by id */
    this.sub.add(this.roundResultService.getResult(roundColumn.entityId).subscribe((result => {
      this.roundResult = result;
      this.dialogKey = roundColumn.columnKey;
      /** waits for the dala load */
      setTimeout(() => {
        this.dialog.show();
      });
    })));
  }

  /**
   * Navigates to speed trial results
   */
  private navigateToSpeedTrialResults(): void {
    this.router.navigate(['/competition', this.competitionId, 'speed-trial', 'results']);
  }

  /**
   * Toolbar click handler
   * @param itemId the clicked item
   */
  protected onToolbarClick(itemId: string): void {
    /** executes the button logic */
    switch (itemId) {
      case 'recalculate':
        this.recalculateResults(this.competitionId);
        break;
      case 'visibility':
        this.updateDisplayStatus(this.competitionId);
        break;
      case 'speed-trials':
        this.navigateToSpeedTrialResults();
        break;
      case 'print':
        this.print();
        break;
    }
  }
}
